import React, { useEffect, useState, useMemo } from 'react';
import {
  Card, Table, Tag, Input, Select, Space, Typography, Button, Row, Col, Tooltip, Badge, Modal,
} from 'antd';
import { SearchOutlined, ReloadOutlined, CheckCircleOutlined, QuestionCircleOutlined, MessageOutlined } from '@ant-design/icons';
import { partnerAPI } from '../services/api';
import ScheduleDetailModal, { STATUS_COLORS_FALLBACK, buildChronologicalEvents } from '../components/ScheduleDetailModal';
import RequestTypeBadge from '../components/RequestTypeBadge';
import InquiryWriteModal from '../components/InquiryWriteModal';
import InquirySection from '../components/InquirySection';
import { useFitTableHeight } from '../hooks/useViewportFit';

const { Title, Text } = Typography;

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => {
  const y = currentYear - i;
  const yy = String(y).slice(2);
  return { value: yy, label: `${yy}년` };
});

// ── 작기 퀵필터 (관리자 시험일정총괄표와 동일 규칙) ──
// 동작물 시즌은 시작연도 기준(시험번호 SRnn=계약·시작연도): SR25I-* = 25~26 동작기, SR26-*W = 26~27 동작기.
// 단, test_year가 수확연도(시작연도+1)로 등록된 레코드도 혼재 → endYears로 보정 매칭.
const currYY = String(currentYear % 100).padStart(2, '0');
const prevYY = String((currentYear - 1) % 100).padStart(2, '0');
const nextYY = String((currentYear + 1) % 100).padStart(2, '0');
const SUMMER_SW = ['S', 'E', 's', 'e', '하'];
const WINTER_SW = ['W', '동', 'w'];
const PERIOD_PRESETS = [
  { key: 'prev_h', label: `${prevYY}년 하작물`,           years: [prevYY, `20${prevYY}`], sw: SUMMER_SW },
  { key: 'prev_d', label: `${prevYY}~${currYY}년 동작물`, years: [prevYY, `20${prevYY}`], sw: WINTER_SW, endYears: [currYY, `20${currYY}`] },
  { key: 'curr_h', label: `${currYY}년 하작물`,           years: [currYY, `20${currYY}`], sw: SUMMER_SW },
  { key: 'curr_d', label: `${currYY}~${nextYY}년 동작물`, years: [currYY, `20${currYY}`], sw: WINTER_SW, endYears: [nextYY, `20${nextYY}`] },
] as { key: string; label: string; years: string[]; sw: string[]; endYears?: string[] }[];
const PERIOD_STYLES = [
  { key: 'prev_h', isDong: false, color: '#d97706', hoverBg: '#fef3c7', gradBg: 'linear-gradient(135deg,#fbbf24,#d97706)' },
  { key: 'prev_d', isDong: true,  color: '#4f46e5', hoverBg: '#eef2ff', gradBg: 'linear-gradient(135deg,#818cf8,#4f46e5)' },
  { key: 'curr_h', isDong: false, color: '#059669', hoverBg: '#ecfdf5', gradBg: 'linear-gradient(135deg,#34d399,#059669)' },
  { key: 'curr_d', isDong: true,  color: '#0369a1', hoverBg: '#e0f2fe', gradBg: 'linear-gradient(135deg,#38bdf8,#0369a1)' },
];

interface TreatmentItem { round_no: number; treatment_date?: string; }
interface SurveyItem    { round_no: number; survey_date?: string; survey_type?: string; }

interface Schedule {
  id: number;
  seq_no: number;
  test_number: string;
  title: string;
  request_type?: string;
  category_name: string;
  location_name: string;
  start_date: string;
  status: string;
  test_year: string;
  summer_winter?: string;
  treatments: TreatmentItem[];
  surveys: SurveyItem[];
  partner_confirmed_at?: string;
  partner_confirmed_by?: string;
}

interface StatusCode { code: string; color: string; is_active: boolean; }

const ScheduleListPage: React.FC = () => {
  /* 표 본문 높이 실측 — 페이지 세로스크롤 없이 페이지네이션까지 뷰포트 안에 */
  const { tableWrapRef, cardWrapRef, bodyHeight } = useFitTableHeight();
  const [data, setData] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [yearFilter, setYearFilter] = useState<string | undefined>(String(currentYear).slice(2));
  const [statusCodes, setStatusCodes] = useState<StatusCode[]>([]);
  const [filterPeriod, setFilterPeriod] = useState<string | null>(null);

  const [detailRecord, setDetailRecord] = useState<Schedule | null>(null);
  const [inquiryTarget, setInquiryTarget] = useState<Schedule | null>(null);
  // 시험별 문의 집계 (배지) — { schedule_id: { total, unread } }
  const [inquiryStats, setInquiryStats] = useState<Record<number, { total: number; unread: number }>>({});
  // 기존 문의가 있는 시험은 목록 모달로 진입 (읽기 + 추가 문의)
  const [inquiryListTarget, setInquiryListTarget] = useState<Schedule | null>(null);

  const loadInquiryStats = React.useCallback(() => {
    partnerAPI.inquiryScheduleSummary()
      .then(({ data }) => {
        const map: Record<number, { total: number; unread: number }> = {};
        (data || []).forEach((r: any) => { map[r.schedule_id] = { total: r.total, unread: r.unread }; });
        setInquiryStats(map);
      })
      .catch(() => setInquiryStats({}));
  }, []);

  useEffect(() => { loadInquiryStats(); }, [loadInquiryStats]);

  // 마스터 상태코드 로드 (색상 포함)
  useEffect(() => {
    partnerAPI.statusCodes()
      .then(res => setStatusCodes(res.data))
      .catch(() => {});
  }, []);

  // API 색상 우선, fallback은 하드코딩 맵
  const statusColorMap = useMemo<Record<string, string>>(() => {
    const map = { ...STATUS_COLORS_FALLBACK };
    statusCodes.forEach(s => { if (s.color) map[s.code] = s.color; });
    return map;
  }, [statusCodes]);

  useEffect(() => { loadSchedules(); }, [statusFilter, yearFilter]);

  const loadSchedules = async () => {
    setLoading(true);
    try {
      const res = await partnerAPI.schedules({ keyword: search, status: statusFilter, test_year: yearFilter });
      setData(res.data);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // 작기 퀵필터 적용 (클라이언트측, 관리자 화면과 동일 매칭 규칙)
  const displayedData = useMemo(() => {
    if (!filterPeriod) return data;
    const preset = PERIOD_PRESETS.find(p => p.key === filterPeriod);
    if (!preset) return data;
    return data.filter(s => {
      const yr = (s.test_year || '').trim();
      const tnYr = (s.test_number || '').match(/SR(\d{2})/i)?.[1] || '';
      const sw = (s.summer_winter || '').trim();
      const yearMatch = preset.years.includes(yr) || preset.years.includes(tnYr);
      const swMatch = preset.sw.includes(sw);
      if (yearMatch && swMatch) return true;
      // 동작기 수확연도 표기 레코드: 시험번호=시작연도(SR26), test_year=이듬해(27) → 해당 동작기 확정
      if (preset.endYears && preset.endYears.includes(yr) && preset.years.includes(tnYr) && !SUMMER_SW.includes(sw)) return true;
      return false;
    });
  }, [data, filterPeriod]);

  // 목록에서 바로 문의/요청 등록 (행 클릭 상세와 분리)
  const inquiryLabel = inquiryTarget
    ? `${inquiryTarget.test_number || ''} · ${inquiryTarget.title || ''}`
    : '';

  const columns = [
    { title: '연번', key: 'rowNo', width: 52, align: 'center' as const,
      render: (_: any, __: Schedule, index: number) => index + 1 },
    {
      title: '시험번호', dataIndex: 'test_number', key: 'test_number', width: 150,
      sorter: (a: Schedule, b: Schedule) => a.test_number.localeCompare(b.test_number),
      render: (text: string) => <Text strong style={{ color: '#92400e', fontSize: 12 }}>{text}</Text>,
    },
    { title: '항목명', dataIndex: 'title', key: 'title', ellipsis: true,
      sorter: (a: Schedule, b: Schedule) => (a.title || '').localeCompare(b.title || '', 'ko') },
    { title: '의뢰구분', dataIndex: 'request_type', key: 'request_type', width: 82, align: 'center' as const,
      sorter: (a: Schedule, b: Schedule) => (a.request_type || '').localeCompare(b.request_type || '', 'ko'),
      render: (v: string) => <RequestTypeBadge value={v} /> },
    { title: '시험구분', dataIndex: 'category_name', key: 'category_name', width: 100, align: 'center' as const,
      sorter: (a: Schedule, b: Schedule) => (a.category_name || '').localeCompare(b.category_name || '', 'ko') },
    { title: '시험장소', dataIndex: 'location_name', key: 'location_name', width: 90, align: 'center' as const,
      sorter: (a: Schedule, b: Schedule) => (a.location_name || '').localeCompare(b.location_name || '', 'ko') },
    { title: '개시일', dataIndex: 'start_date', key: 'start_date', width: 90, align: 'center' as const,
      sorter: (a: Schedule, b: Schedule) => (a.start_date || '').localeCompare(b.start_date || '') },
    {
      title: '처리', key: 'treatment_progress', width: 60, align: 'center' as const,
      render: (_: any, record: Schedule) => {
        const { events } = buildChronologicalEvents(record);
        const items = events.filter(e => e.type === '처리');
        const filled = items.filter(e => e.completed).length;
        const total = items.length;
        const color = total === 0 ? '#94a3b8' : filled === total ? '#10b981' : '#f59e0b';
        return <span style={{ fontSize: 12, fontWeight: 600, color }}>{filled}/{total}</span>;
      },
    },
    {
      title: '약효조사', key: 'survey_yakho', width: 72, align: 'center' as const,
      render: (_: any, record: Schedule) => {
        const { events } = buildChronologicalEvents(record);
        const items = events.filter(e => e.type === '약효조사');
        const filled = items.filter(e => e.completed).length;
        const total = items.length;
        const color = total === 0 ? '#94a3b8' : filled === total ? '#10b981' : '#f59e0b';
        return <span style={{ fontSize: 12, fontWeight: 600, color }}>{filled}/{total}</span>;
      },
    },
    {
      title: '약해조사', key: 'survey_yakhe', width: 72, align: 'center' as const,
      render: (_: any, record: Schedule) => {
        const { events } = buildChronologicalEvents(record);
        const items = events.filter(e => e.type === '약해조사');
        const filled = items.filter(e => e.completed).length;
        const total = items.length;
        if (total === 0) return <span style={{ fontSize: 11, color: '#cbd5e1' }}>-</span>;
        const color = filled === total ? '#ea580c' : '#f97316';
        return <span style={{ fontSize: 12, fontWeight: 600, color }}>{filled}/{total}</span>;
      },
    },
    {
      title: '상태', dataIndex: 'status', key: 'status', width: 110, align: 'center' as const,
      sorter: (a: Schedule, b: Schedule) => (a.status || '').localeCompare(b.status || '', 'ko'),
      render: (status: string) => (
        <Tag color={statusColorMap[status] || 'default'} style={{ fontSize: 11 }}>{status}</Tag>
      ),
    },
    {
      title: '파트너확인', dataIndex: 'partner_confirmed_at', key: 'partner_confirmed_at', width: 100, align: 'center' as const,
      sorter: (a: Schedule, b: Schedule) => (a.partner_confirmed_at || '').localeCompare(b.partner_confirmed_at || ''),
      render: (val: string) => val
        ? <CheckCircleOutlined style={{ color: '#10b981', fontSize: 16 }} />
        : <span style={{ color: '#d1d5db', fontSize: 11 }}>미확인</span>,
    },
    {
      title: '문의', key: 'inquiry', width: 62, align: 'center' as const,
      render: (_: any, record: Schedule) => {
        const stat = inquiryStats[record.id];
        const total = stat?.total || 0;
        const unread = stat?.unread || 0;
        const tip = unread > 0
          ? `새 답변 ${unread}건 — 클릭하면 확인할 수 있습니다`
          : total > 0
            ? `등록한 문의/요청 ${total}건 (답변 모두 확인함)`
            : '이 시험에 대한 문의/요청 등록';
        // 새 답변 → 빨강(주목), 확인 완료된 문의 → 앰버(정보), 없음 → 회색 아이콘만
        return (
          <Tooltip title={tip}>
            <Badge
              count={unread > 0 ? unread : total}
              size="small"
              offset={[-3, 3]}
              styles={{
                indicator: unread > 0
                  ? { boxShadow: '0 0 0 2px #fff', fontWeight: 700 }
                  : { background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a',
                      boxShadow: 'none', fontWeight: 700 },
              }}
            >
              <Button
                type="text" size="small"
                icon={
                  <MessageOutlined style={{
                    fontSize: 16,
                    color: unread > 0 ? '#ef4444' : total > 0 ? '#d97706' : '#cbd5e1',
                  }} />
                }
                onClick={(e) => {
                  e.stopPropagation();
                  // 문의가 있으면 목록(읽기) 모달, 없으면 곧바로 작성 모달
                  if (total > 0) setInquiryListTarget(record);
                  else setInquiryTarget(record);
                }}
              />
            </Badge>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        <Title level={4} style={{ margin: 0, color: '#92400e' }}>시험 일정</Title>
        <Text type="secondary">의뢰하신 시험 일정을 확인하세요</Text>
      </div>

      <div ref={cardWrapRef}>
      <Card size="small">
        {/* ── 작기 퀵필터 바 ── */}
        <div style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
          padding: '10px 14px', marginBottom: 12,
          background: 'linear-gradient(to right, #fffbeb, #fef3c7)',
          border: '1px solid #fde68a', borderRadius: 10,
        }}>
          <span style={{
            fontSize: 11, fontWeight: 700, color: '#b45309',
            letterSpacing: '0.08em', textTransform: 'uppercase', marginRight: 2,
          }}>
            작기
          </span>
          <Tooltip
            overlayStyle={{ maxWidth: 340 }}
            placement="bottomLeft"
            title={
              <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>작기(재배 시기)별 빠른 필터</div>
                <div>☀️ <b>하작물</b> — 봄~가을에 개시·완결되는 당해년도 시험</div>
                <div>🧊 <b>동작물</b> — 가을에 시작해 이듬해 마무리되는 시험. 시즌이 2개년에 걸치므로 <b>시작연도~수확연도</b>로 표기합니다 (예: {currYY}~{nextYY}년).</div>
                <div style={{ marginTop: 4, opacity: 0.85 }}>
                  · 작기가 입력되지 않은 시험은 '전체'에서만 표시됩니다.
                </div>
              </div>
            }
          >
            <QuestionCircleOutlined style={{ fontSize: 12, color: '#d6b45f', cursor: 'pointer', marginRight: 2 }} />
          </Tooltip>
          <div style={{ width: 1, height: 18, background: '#fcd34d', margin: '0 4px' }} />
          <button
            onClick={() => { setFilterPeriod(null); setYearFilter(String(currentYear).slice(2)); }}
            style={{
              padding: '5px 14px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
              fontWeight: !filterPeriod ? 700 : 500,
              border: `1.5px solid ${!filterPeriod ? '#b45309' : '#fde68a'}`,
              background: !filterPeriod ? '#b45309' : '#fff',
              color: !filterPeriod ? '#fff' : '#b8a06a',
              boxShadow: !filterPeriod ? '0 2px 8px rgba(180,83,9,0.25)' : 'none',
              transition: 'all 0.15s ease',
            }}
          >
            전체
          </button>
          {PERIOD_STYLES.map(st => {
            const p = { ...st, label: PERIOD_PRESETS.find(x => x.key === st.key)!.label };
            const isActive = filterPeriod === p.key;
            return (
              <button
                key={p.key}
                onClick={() => {
                  if (isActive) { setFilterPeriod(null); setYearFilter(String(currentYear).slice(2)); }
                  else { setFilterPeriod(p.key); setYearFilter(undefined); }
                }}
                style={{
                  padding: '5px 14px', borderRadius: 20, fontSize: 12.5, cursor: 'pointer',
                  fontWeight: isActive ? 700 : 500,
                  border: `1.5px solid ${isActive ? 'transparent' : p.color + '55'}`,
                  background: isActive ? p.gradBg : p.hoverBg,
                  color: isActive ? '#fff' : p.color,
                  boxShadow: isActive ? `0 3px 10px ${p.color}45` : 'none',
                  transition: 'all 0.15s ease',
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                }}
              >
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  background: isActive ? 'rgba(255,255,255,0.25)' : (p.isDong ? '#dbeafe' : '#dcfce7'),
                  border: `1px solid ${isActive ? 'rgba(255,255,255,0.45)' : (p.isDong ? '#93c5fd' : '#86efac')}`,
                  borderRadius: 8, padding: '1px 5px',
                  fontSize: 10, fontWeight: 800,
                  color: isActive ? '#fff' : (p.isDong ? '#1d4ed8' : '#166534'),
                  lineHeight: 1.4,
                }}>
                  <span style={{ fontSize: 10, lineHeight: 1 }}>{p.isDong ? '🧊' : '☀️'}</span>
                  {p.isDong ? '동' : '하'}
                </span>
                {p.label}
                {isActive && (
                  <span style={{
                    marginLeft: 2, fontSize: 10, opacity: 0.85,
                    background: 'rgba(255,255,255,0.25)', borderRadius: '50%',
                    width: 14, height: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>✕</span>
                )}
              </button>
            );
          })}
          {filterPeriod && (
            <span style={{
              marginLeft: 6, fontSize: 11, color: '#92400e',
              background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, padding: '2px 8px',
            }}>
              {displayedData.length}건
            </span>
          )}
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #fffbeb, #fef9e7)',
          border: '1px solid #fde68a', borderRadius: 10,
          padding: '8px 12px', marginBottom: 8,
        }}>
          <Row gutter={[10, 10]} align="middle">
            <Col xs={24} sm={8} md={6}>
              <Select
                placeholder="년도" allowClear style={{ width: '100%' }}
                value={yearFilter} onChange={v => setYearFilter(v)} options={YEAR_OPTIONS}
              />
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Select
                placeholder="진행상태" allowClear style={{ width: '100%' }}
                value={statusFilter} onChange={setStatusFilter}
                optionLabelProp="label"
              >
                {(statusCodes.length > 0 ? statusCodes.filter(s => s.is_active) : Object.entries(STATUS_COLORS_FALLBACK).map(([code, color]) => ({ code, color, is_active: true }))).map(s => (
                  <Select.Option key={s.code} value={s.code} label={s.code}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <div style={{ width: 9, height: 9, borderRadius: '50%', background: s.color || '#94a3b8', flexShrink: 0 }} />
                      <span style={{ fontSize: 13 }}>{s.code}</span>
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="시험번호 또는 항목명"
                prefix={<SearchOutlined style={{ color: '#d97706' }} />}
                value={search} onChange={e => setSearch(e.target.value)}
                onPressEnter={loadSchedules} allowClear
              />
            </Col>
            <Col>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={loadSchedules}
                  style={{ background: '#f59e0b', borderColor: '#f59e0b' }}>검색</Button>
                <Button icon={<ReloadOutlined />} onClick={() => {
                  setSearch(''); setStatusFilter(undefined); setFilterPeriod(null);
                  setYearFilter(String(currentYear).slice(2));
                }}>초기화</Button>
              </Space>
            </Col>
          </Row>
        </div>

        <div ref={tableWrapRef}>
        <Table
          columns={columns}
          dataSource={displayedData}
          rowKey="id"
          loading={loading}
          size="middle"
          showSorterTooltip={false}
          scroll={{ y: bodyHeight }}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}건`,
          }}
          onRow={record => ({ onClick: () => setDetailRecord(record), style: { cursor: 'pointer' } })}
          rowClassName={(_, idx) => idx % 2 === 1 ? 'table-row-alt' : ''}
        />
        </div>
      </Card>
      </div>

      {/* 목록에서 바로 등록하는 문의/요청 작성 모달 */}
      <InquiryWriteModal
        open={!!inquiryTarget}
        onCancel={() => setInquiryTarget(null)}
        onCreated={loadInquiryStats}
        scheduleId={inquiryTarget?.id ?? null}
        scheduleLabel={inquiryLabel}
      />

      {/* 기존 문의가 있는 시험 — 스레드 확인 + 추가 문의 */}
      <Modal
        open={!!inquiryListTarget}
        onCancel={() => { setInquiryListTarget(null); loadInquiryStats(); }}
        footer={<Button onClick={() => { setInquiryListTarget(null); loadInquiryStats(); }}>닫기</Button>}
        width={700}
        title={
          <Space size={8}>
            <MessageOutlined style={{ color: '#f59e0b' }} />
            <span style={{ fontWeight: 700 }}>문의 / 요청</span>
            <Tag color="orange" style={{ margin: 0 }}>{inquiryListTarget?.test_number}</Tag>
          </Space>
        }
        styles={{ body: { maxHeight: '68vh', overflowY: 'auto' } }}
      >
        {inquiryListTarget && (
          <InquirySection
            key={inquiryListTarget.id}
            bare
            scheduleId={inquiryListTarget.id}
            scheduleLabel={`${inquiryListTarget.test_number || ''} · ${inquiryListTarget.title || ''}`}
            onChanged={loadInquiryStats}
          />
        )}
      </Modal>

      {/* Detail Modal (공용 컴포넌트) */}
      <ScheduleDetailModal
        open={!!detailRecord}
        scheduleId={detailRecord?.id ?? null}
        titleRecord={detailRecord}
        statusColorMap={statusColorMap}
        onClose={() => setDetailRecord(null)}
        onChanged={loadSchedules}
      />
    </div>
  );
};

export default ScheduleListPage;
